import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { createContext, useContext } from 'react';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'border-border-strong text-muted-foreground',
        destructive:
          'border-destructive-foreground text-destructive-foreground [&>svg]:text-destructive-foreground',
        warning:
          'bg-warn-background border-warn-foreground text-warn-foreground [&>svg]:text-warn-foreground'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

const AlertVariantContext = createContext<'default' | 'destructive' | 'warning'>('default');
export const useAlertVariant = () => useContext(AlertVariantContext);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant = 'default', ...props }, ref) => (
  <AlertVariantContext.Provider value={variant ?? 'default'}>
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
  </AlertVariantContext.Provider>
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  (props, ref) => {
    const variant = useAlertVariant();
    return (
      <h5
        ref={ref}
        className={cn(
          'mb-2 font-medium leading-none tracking-tight',
          variant === 'default' && 'text-primary',
          props.className
        )}
        {...props}
      />
    );
  }
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(
  (
    { className, ...props }: React.HTMLAttributes<HTMLParagraphElement>,
    ref: React.Ref<HTMLParagraphElement>
  ) => <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
